import { useEffect, useState } from "react";
import api, { fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Edit2 } from "lucide-react";

const CURRENCIES = ["TRY", "USD", "EUR"];
const INCOME_CATS = ["Satış Geliri", "Hizmet Geliri", "Faiz Geliri", "Kira Geliri", "Diğer Gelir"];
const EXPENSE_CATS = ["Personel Maaşı", "Kira", "Elektrik/Su", "İnternet", "Yakıt", "Pazarlama", "Yazılım Lisans", "Ofis Malzeme", "Vergi/SGK", "Diğer Gider"];

export default function TransactionsPage({ type }) {
  const title = type === "gelir" ? "Gelirler" : "Giderler";
  const cats = type === "gelir" ? INCOME_CATS : EXPENSE_CATS;
  const [rows, setRows] = useState([]);
  const [accs, setAccs] = useState([]);
  const [cons, setCons] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    return { type, amount: "", currency: "TRY", date: new Date().toISOString().slice(0, 10), category: cats[0], account_id: "", contact_id: "", note: "" };
  }

  const load = async () => {
    const [r, a, c] = await Promise.all([
      api.get(`/transactions?type=${type}`),
      api.get("/accounts"),
      api.get("/contacts"),
    ]);
    setRows(r.data); setAccs(a.data); setCons(c.data);
  };

  useEffect(() => { setForm(empty()); load(); }, [type]);

  const save = async () => {
    const payload = { ...form, amount: parseFloat(form.amount), account_id: form.account_id || null, contact_id: form.contact_id || null };
    try {
      if (edit) await api.put(`/transactions/${edit.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(edit ? "Güncellendi" : `${type === "gelir" ? "Gelir" : "Gider"} eklendi`);
      setOpen(false); setEdit(null); setForm(empty()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Silindi"); load();
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({ ...row, account_id: row.account_id || "", contact_id: row.contact_id || "" });
    setOpen(true);
  };

  return (
    <div className="space-y-5 nf-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-num text-slate-500">{type}</div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">{title}</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEdit(null); setForm(empty()); } }}>
          <DialogTrigger asChild>
            <Button data-testid={`add-${type}-btn`} className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> Yeni {type === "gelir" ? "Gelir" : "Gider"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121722] border-slate-800 max-w-lg">
            <DialogHeader><DialogTitle className="font-head">{edit ? "Düzenle" : `Yeni ${type === "gelir" ? "Gelir" : "Gider"}`}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tutar</Label><Input data-testid="tx-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-slate-900/60 border-slate-800 mt-1" /></div>
              <div><Label>Döviz</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tarih</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-slate-900/60 border-slate-800 mt-1" /></div>
              <div><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Hesap</Label>
                <Select value={form.account_id || "none"} onValueChange={(v) => setForm({ ...form, account_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800 mt-1"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Yok</SelectItem>{accs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Cari Hesap</Label>
                <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-800 mt-1"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Yok</SelectItem>{cons.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Açıklama</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-slate-900/60 border-slate-800 mt-1" /></div>
            </div>
            <Button data-testid="tx-save" onClick={save} className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 font-semibold w-full">Kaydet</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="nf-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/40 border-b border-slate-800">
              <tr className="text-left text-xs uppercase tracking-widest font-mono-num text-slate-500">
                <th className="px-4 py-3">Tarih</th>
                <th className="px-4 py-3">Kategori</th>
                <th className="px-4 py-3">Açıklama</th>
                <th className="px-4 py-3 text-right">Tutar</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-10">Kayıt yok</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-800/20">
                  <td className="px-4 py-3 font-mono-num text-slate-400">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3">{r.category}</td>
                  <td className="px-4 py-3 text-slate-400 truncate max-w-xs">{r.note}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold" style={{ color: type === "gelir" ? "#10B981" : "#F43F5E" }}>
                    {type === "gelir" ? "+" : "−"}{r.amount.toLocaleString("tr-TR")} {r.currency}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(r)} className="p-1.5 text-slate-500 hover:text-cyan-400"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-tx-${r.id}`} onClick={() => del(r.id)} className="p-1.5 text-slate-500 hover:text-coral-400" style={{ color: "" }}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
